import { useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { PlatformHeader } from '../../../shared/platform/PlatformHeader';
import '../../overview/ui/overview.css';
import { createConfigurationAdapter } from '../adapters/configuration-http';
import { createEvaluationCountsAdapter } from '../adapters/evaluation-counts-http';
import { createForecastAdapter } from '../adapters/forecasting-http';
import { createMonitoringAdapter } from '../adapters/http';
import {
  parseRiskRoute,
  riskHref,
  type RiskPageProps,
  type RiskRoute,
} from '../consumer/workbench';
import { ForecastDetailPage } from './details/ForecastDetailPage';
import { WarningDetailPage } from './details/WarningDetailPage';
import { EvaluationPage } from './management/EvaluationPage';
import { NetworkPage } from './management/NetworkPage';
import { MonitoringWriteSession } from './MonitoringWriteSession';
import './normal-monitoring.css';
import { RiskOverview } from './overview/RiskOverview';
function browserRoute(): RiskRoute {
  return typeof window === 'undefined'
    ? { kind: 'overview', areaId: '' }
    : parseRiskRoute(window.location.pathname, window.location.search);
}
const titles: Record<RiskRoute['kind'], string> = {
  overview: '风险与预警',
  warning: '观测预警详情',
  forecast: '趋势预测详情',
  network: '区域与来源',
  evaluation: '模型效果与回验',
  missing: '未找到工作台页面',
};
export function MonitoringRiskView(props: Omit<RiskPageProps, 'areaId'>) {
  const [route, setRoute] = useState(browserRoute);
  useEffect(() => {
    const update = () => setRoute(browserRoute());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  const scoped = { ...props, areaId: route.areaId };
  return (
    <div className="p01 nm-page nm-vs04-page">
      <PlatformHeader activePage="monitoring" />
      <main id="nm-main" tabIndex={-1} className="nm-vs04-main">
        <p className="nm-vs04-breadcrumb">国家级 AI 消防平台 / 常态运行 / 风险与预警</p>
        <div className="nm-vs04-heading">
          <div>
            <h1>{titles[route.kind]}</h1>
            <p>
              {route.kind === 'overview'
                ? '持续观察区域变化，区分已发生异常与未来预测关注。'
                : '按对象查看实际后端记录、依据与变化。'}
            </p>
          </div>
          <span className="nm-vs04-mode">本地合成监测</span>
        </div>
        <p className="nm-vs04-disclaimer">
          合成监测与预测演示 · SYNTHETIC / NON-OPERATIONAL / NOT FOR RESCUE USE
        </p>
        <MonitoringWriteSession />
        <nav className="nm-vs04-nav" aria-label="风险与预警工作台">
          <a
            href={riskHref('', route.areaId)}
            aria-current={route.kind === 'overview' ? 'page' : undefined}
          >
            运行总览
          </a>
          {route.kind === 'warning' && (
            <a
              href={riskHref(`/warnings/${encodeURIComponent(route.id)}`, route.areaId)}
              aria-current="page"
            >
              预警详情
            </a>
          )}
          {route.kind === 'forecast' && (
            <a
              href={riskHref(`/forecasts/${encodeURIComponent(route.id)}`, route.areaId)}
              aria-current="page"
            >
              预测详情
            </a>
          )}
          <a
            href={riskHref('/network', route.areaId)}
            aria-current={route.kind === 'network' ? 'page' : undefined}
          >
            区域与来源
          </a>
          <a
            href={riskHref('/evaluation', route.areaId)}
            aria-current={route.kind === 'evaluation' ? 'page' : undefined}
          >
            模型效果
          </a>
        </nav>
        {route.kind !== 'overview' && (
          <a className="nm-vs04-back" href={riskHref('', route.areaId)}>
            ← 返回风险与预警总览
          </a>
        )}
        {route.kind === 'overview' && <RiskOverview {...scoped} />}
        {route.kind === 'warning' && (
          <WarningDetailPage
            key={route.id}
            {...scoped}
            id={route.id}
            {...(route.revision ? { revision: route.revision } : {})}
          />
        )}
        {route.kind === 'forecast' && (
          <ForecastDetailPage
            key={route.id}
            {...scoped}
            id={route.id}
            {...(route.horizon ? { horizon: route.horizon } : {})}
          />
        )}
        {route.kind === 'network' && <NetworkPage {...scoped} />}
        {route.kind === 'evaluation' && <EvaluationPage {...scoped} />}
        {route.kind === 'missing' && (
          <section role="alert" className="nm-vs04-card">
            <h2>404 · 页面或对象链接无效</h2>
            <p>请返回总览，从真实记录重新进入。当前链接不表示对象不存在或区域安全。</p>
          </section>
        )}
        <footer className="nm-vs04-footer">
          P2-NO-VS04 · 信息分层工作台 · 时间与状态以实际后端查询为准
        </footer>
      </main>
    </div>
  );
}
export function NormalMonitoringPage() {
  const ports = useMemo(() => {
    const options = { baseOrigin: apiBaseOrigin() };
    return {
      port: createMonitoringAdapter(options),
      configurationPort: createConfigurationAdapter(options),
      forecastPort: createForecastAdapter(options),
      countsPort: createEvaluationCountsAdapter(options),
    };
  }, []);
  return <MonitoringRiskView {...ports} />;
}
